import React, { useCallback, useEffect, useState, useContext } from 'react';
import { arrayOf, shape, string } from 'prop-types';
import classNames from 'classnames';

import { useOnResize } from 'hooks/use-on-resize';

import pathToUrl from 'content/components/CategoryLinks/cms-path-to-url';

import Button from '@johnlewispartnership/wtr-ingredients/ingredients/Button';

import EventMonitor from 'components/EventMonitor/EventMonitor';

import { RecipesContext } from 'components/RecipesLanding/util';
import CardNavLink from './CardNavLink';

import styles from './index.scss';

const perRows = [3, 2, 1];
const maxRows = [2, 3, 3];

const cardNavDefaultPropNavigationLinks = [];
const cardNavDefaultPropBreakpointClasses = [];

const CardNav = ({
  ariaLabel = null,
  navigationLinks = cardNavDefaultPropNavigationLinks,
  breakpointClasses = cardNavDefaultPropBreakpointClasses,
}) => {
  const [breakPt, setBreakPt] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const { isAllCategoriesPage } = useContext(RecipesContext);

  /* istanbul ignore next */
  const onResize = useCallback(() => {
    const { innerWidth: width } = window;

    let w = 0;
    if (width < 544) {
      w = 2;
    } else if (width < 768) {
      w = 1;
    }
    setBreakPt(w);
  }, []);

  useOnResize(onResize);

  // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/set-state-in-effect
  useEffect(onResize, []);

  // TODO: review code from WPIP-32333. reimplement transition from commit:415a882e2fb9cd89a0c51cf672687929bcb5c2ce

  const formattedNavLinks = navigationLinks.map(({ title, path, categoryId, thumbnail }) => {
    return path ? { title, thumbnail, path: pathToUrl(path) } : { title, thumbnail, categoryId };
  });

  const renderShowAllBtn =
    formattedNavLinks.length > maxRows[breakPt] * perRows[breakPt] && !isAllCategoriesPage;

  return (
    formattedNavLinks.length !== 0 && (
      <nav className={classNames(breakpointClasses)}>
        <div aria-label={ariaLabel} className={styles.categoryLinks} data-testid="card-nav">
          <div className={styles.crop}>
            <ul data-test="card-nav-links" className={styles.list}>
              {formattedNavLinks.map(({ path, categoryId, thumbnail, title }, i) => {
                const visible =
                  expanded || isAllCategoriesPage || i < maxRows[breakPt] * perRows[breakPt];

                return (
                  <CardNavLink
                    key={title}
                    path={path}
                    title={title}
                    visible={visible}
                    thumbnail={thumbnail}
                    categoryId={categoryId}
                  />
                );
              })}
            </ul>
          </div>
          {renderShowAllBtn && (
            <div className={styles.cta} data-testid="see-more-expander">
              <div className={styles.button}>
                <EventMonitor
                  actionType="redirect"
                  customData={{
                    'data-category': 'Browse',
                  }}
                  originComponent="show all"
                  shortDescription="show all button"
                >
                  <Button
                    data-test="navigation-card-cta"
                    theme="secondary"
                    label={expanded ? `Show less` : `Show all`}
                    onClick={() => setExpanded(!expanded)}
                  />
                </EventMonitor>
              </div>
            </div>
          )}
        </div>
      </nav>
    )
  );
};

CardNav.propTypes = {
  ariaLabel: string,
  navigationLinks: arrayOf(
    shape({
      title: string.isRequired,
      path: string,
      categoryId: string,
    }),
  ),
  breakpointClasses: arrayOf(string),
};

export default CardNav;
